// Global Instructions Rule Applied! - 2026-05-06

import { FieldTypeEnum } from '../../core/components/InputManager/types.ts'
import { formatDateForExport } from '../../core/utils/exportFormatUtils.ts'
import {
  getAccountRoleChoiceLabelById,
  getFundNameById,
  getLookupLabelFromGlobalConfig,
  getPipelineStageNameById
} from '../../core/utils/lookupLabelFromStore.ts'
import { getTableCellValueForRecord } from '../../core/utils/utilities.ts'
import { UUID_REGEX } from '../globals.ts'
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'

export type ReportingGridExcelPayload = {
  data: Record<string, unknown>[]
  columns: string[]
  dateColumns: string[]
  percentageColumns: string[]
}

function isTasksFieldType(fieldType: string | undefined): boolean {
  return fieldType === FieldTypeEnum.TASKS || fieldType === 'tasks'
}

function isSelectLikeFieldType(fieldType: string | undefined): boolean {
  return (
    fieldType === FieldTypeEnum.SELECT ||
    fieldType === 'select' ||
    fieldType === 'multiselect'
  )
}

/**
 * `cfg.reporting_column_defaults` columns with `field_value_ref` set carry an
 * id at `dataKey` and the human-readable display at `labelKey` (e.g. Fund:
 * `funds__id` / `funds__name`; Industry: `accounts__industry` /
 * `accounts__industry__label`). For text/select-style columns the label is what
 * belongs in Excel — the id is a UUID nobody wants in a download. For
 * numeric/date types the raw value at `dataKey` is what Excel needs so cells
 * sort and aggregate, so those keep raw-first ordering even when `labelKey` is
 * set (e.g. `cost_incurred_fmt` = "$300,000", `cost_incurred` = 300000).
 */
function prefersLabelOverDataKeyForExport(config: ViewConfigColumn): boolean {
  if (!config.labelKey) return false
  if (isSelectLikeFieldType(config.fieldType)) return true
  return config.fieldType === FieldTypeEnum.TEXT || config.fieldType === 'text'
}

function isInvestmentStatsColumn(config: ViewConfigColumn): boolean {
  const vk = config.valueKey
  const dk = config.dataKey
  const lk = config.labelKey
  return (
    vk === 'prog__investment_stats' ||
    (typeof vk === 'string' && vk.startsWith('prog__investment_stats.')) ||
    dk === 'prog__investment_stats' ||
    (typeof lk === 'string' && lk.includes('prog__investment_stats'))
  )
}

/** Deals `pipeline_deals__stage`, prospects `pipeline_prospects__stage`, etc. */
function isPipelineStageColumn(config: ViewConfigColumn): boolean {
  const s = `${config.valueKey}|${config.dataKey ?? ''}|${config.labelKey ?? ''}`
  return /pipeline_[a-z_]+__stage/.test(s)
}

function resolveSelectIdForExport(id: string, config: ViewConfigColumn): string {
  const fromLookup = getLookupLabelFromGlobalConfig(id)
  if (fromLookup) return fromLookup
  if (config.valueKey === 'role') {
    const r = getAccountRoleChoiceLabelById(id)
    if (r) return r
  }
  if (isInvestmentStatsColumn(config)) {
    const f = getFundNameById(id)
    if (f) return f
  }
  if (isPipelineStageColumn(config)) {
    const p = getPipelineStageNameById(id)
    if (p) return p
  }
  return id
}

/**
 * Lookup/multiselect ids: `lookup_details` plus Companies-only `account_role_choices` / `funds`
 * (same as table option sources).
 */
function resolveLookupUuidsInExportValue(value: unknown, config: ViewConfigColumn): unknown {
  if (!isSelectLikeFieldType(config.fieldType)) return value
  if (typeof value === 'string' && UUID_REGEX.test(value)) {
    return resolveSelectIdForExport(value, config)
  }
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string' && UUID_REGEX.test(item)) {
        return resolveSelectIdForExport(item, config)
      }
      return item
    })
  }
  return value
}

/**
 * Plain objects as `dataKey` (e.g. `prog__investment_stats`) are not a cell value — skip
 * to dotted `valueKey` paths like `prog__investment_stats.funds__name`.
 */
function isUsableFirstExportCandidate(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false
  if (Array.isArray(v)) return true
  if (v instanceof Date) return true
  if (typeof v === 'number' || typeof v === 'boolean') return true
  if (typeof v === 'string') return true
  if (typeof v === 'object') return false
  return false
}

/**
 * Contact / user-entry rows from RPC match {@link fundraisingTableMapper} CONTACT fields:
 * `prog__display_name`, `contacts__first_name` / `contacts__last_name`, `contacts__email`, then `label`.
 */
function formatContactLikeObjectForExcel(o: Record<string, unknown>): string | null {
  const progDisplay = o['prog__display_name']
  if (typeof progDisplay === 'string' && progDisplay.trim()) return progDisplay.trim()

  const first = o['contacts__first_name']
  const last = o['contacts__last_name']
  const namePart = `${typeof first === 'string' ? first : ''} ${typeof last === 'string' ? last : ''}`.trim()
  if (namePart) return namePart

  const email = o['contacts__email']
  if (typeof email === 'string' && email.trim()) return email.trim()

  const lbl = o.label ?? o.name
  if (typeof lbl === 'string' && lbl.trim()) return lbl.trim()

  const val = o.value
  if (typeof val === 'string' && val.trim() && !UUID_REGEX.test(val)) return val.trim()

  if (o['contacts__id'] != null) return ''

  return null
}

function objectToExcelScalar(o: Record<string, unknown>): string {
  const contact = formatContactLikeObjectForExcel(o)
  if (contact !== null) return contact

  const lbl = o.label ?? o.name
  if (typeof lbl === 'string' && lbl.trim()) return lbl.trim()
  if (o.value != null && typeof o.value === 'string' && o.value.trim()) return o.value

  return String(o)
}

function normalizeValueForExcelCell(value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => {
        if (item === null || item === undefined) return null
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          return String(item)
        }
        if (typeof item === 'object') {
          return objectToExcelScalar(item as Record<string, unknown>)
        }
        return String(item)
      })
      .filter((p): p is string => p != null && p !== '')
    return parts.join(', ')
  }
  if (typeof value === 'object') {
    return objectToExcelScalar(value as Record<string, unknown>)
  }
  return value
}

/**
 * Single-cell value for Excel export: path order depends on column shape.
 * - Text/select/multiselect with a `labelKey` (cfg `field_name` when `field_value_ref`
 *   is set): **label path first** so ids (e.g. Fund / Industry / pipeline-stage UUIDs)
 *   do not win over the `*_name` / `*__label` display column; then `{dataKey}__label`,
 *   then raw `dataKey`.
 * - Otherwise: raw `dataKey` first, then `{dataKey}__label`, then `labelKey`. Numeric
 *   / date columns with `labelKey` (e.g. `cost_incurred_fmt`) intentionally fall here
 *   so Excel receives the raw numeric/date value, not the pre-formatted string.
 * Dates are formatted; select uuids resolve via lookups + account roles, funds, pipeline stages.
 */
export function resolveReportingGridExportCellValue(
  record: Record<string, unknown>,
  config: ViewConfigColumn
): unknown {
  if (isTasksFieldType(config.fieldType)) {
    return undefined
  }

  if (config.valueKey === 'irr_fmt') {
    const irr = record.irr
    const num = typeof irr === 'number' && !Number.isNaN(irr) ? irr : null
    return num
  }

  const mod = config.moduleKey
  const dk = config.dataKey ?? config.valueKey

  const fromValue = dk ? getTableCellValueForRecord(record, dk, mod) : undefined
  const fromValueLabelSuffix = dk ? getTableCellValueForRecord(record, `${dk}__label`, mod) : undefined
  const fromLabelKey = config.labelKey
    ? getTableCellValueForRecord(record, config.labelKey, mod)
    : undefined

  // When reporting_columns sets field_value_ref + field_name, raw is often an id; Excel should match the grid (label path first).
  const ordered: unknown[] = prefersLabelOverDataKeyForExport(config)
    ? [fromLabelKey, fromValueLabelSuffix, fromValue]
    : [fromValue, fromValueLabelSuffix, fromLabelKey]
  let out: unknown
  const picked = ordered.find((v) => isUsableFirstExportCandidate(v))
  if (picked !== undefined) {
    out = picked
  } else {
    out = ordered.find((v) => v !== undefined && v !== null) ?? undefined
  }

  if (config.fieldType === 'date') {
    out = formatDateForExport(out)
  } else {
    out = resolveLookupUuidsInExportValue(out, config)
    out = normalizeValueForExcelCell(out)
  }

  return out
}

/**
 * Maps reporting grid column configs and raw records into rows/column metadata for {@link exportJsonToExcel}.
 * Lookup/select-style fields resolve to labels (aligned with table display). Task columns are omitted.
 */
export function buildReportingGridExcelPayload(
  gridColumns: ViewConfigColumn[],
  records: Record<string, unknown>[]
): ReportingGridExcelPayload {
  const exportableColumns = gridColumns.filter((c) => !isTasksFieldType(c.fieldType))

  const exportData = records.map((record) => {
    const row: Record<string, unknown> = {}
    exportableColumns.forEach((config) => {
      const cell = resolveReportingGridExportCellValue(record, config)
      row[config.columnTitle] = cell === undefined ? null : cell
    })
    return row
  })

  const columns = exportableColumns.map((c) => c.columnTitle)
  const dateColumns = exportableColumns.filter((c) => c.fieldType === 'date').map((c) => c.columnTitle)
  const percentageColumns = exportableColumns.filter((c) => c.valueKey === 'irr_fmt').map((c) => c.columnTitle)

  return {
    data: exportData,
    columns,
    dateColumns,
    percentageColumns
  }
}
